import { flexCenter, flexColumn, marginToChildren } from "@components/mixins";
import SEO from "@components/SEO/SEO.component";
import SpinnerComponent from "@components/Spinner/Spinner.component";
import { useAnimationLayout } from "@utils/animationUtils";
import { convertToAuthors, getAllSizesPNG } from "@utils/helpers";
import { useFetch } from "@utils/hooks";
import { useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { animationLayout } from "./animationLayout";

const ArticleContainer = styled.article`
    ${flexColumn};
    ${flexCenter};
    ${marginToChildren()};
    width: 100%;
    height: auto;

    p {
        font-family: ${({ theme }) => theme.ff.Cormorant};
    }

    p.bordered {
        padding-bottom: 15px;
        border-bottom: 1px solid ${({ theme }) => theme.colors.light_grey2};
    }

    .spacer {
        width: 100%;
        height: 1px;
        margin: 0 0 30px 0;
        background-color: ${({ theme }) => theme.colors.light_grey2};
    }
`;

const HeaderContainer = styled.header`
    position: relative;
    width: 100%;
    height: auto;
    min-height: 500px;
    max-height: 900px;
    margin: 15px auto 50px;

    @media screen and (max-width: 830px) {
        margin: 15px auto;
    }
`;

const Photo = styled.img`
    display: block;
    max-width: 85%;
    max-height: 900px;
    object-fit: contain;
    object-position: left center;
    opacity: 0;

    @media screen and (max-width: 1050px) {
        max-width: 100%;
        margin: 20px auto;
        width: 100%;
        padding: 0 15px;
    }
    @media screen and (max-width: 830px) {
        max-width: 350px;
        margin: 20px auto;
        width: 100%;
        padding: 0 15px;
    }
`;

export const DescriptionElement = styled.div`
    ${flexColumn};
    ${marginToChildren()};
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 40%;
    padding: 30px;
    background-color: ${({ theme }) => theme.colors.white};
    opacity: 0;

    p {
        line-height: 135%;
        text-align: justify;
    }

    @media screen and (max-width: 1050px) {
        position: static;
        inset: unset;
        transform: none;
        margin: 0 auto;
        width: 90%;
    }

    @media screen and (max-width: 830px) {
    }
`;

const Title = styled.h2`
    margin-bottom: 25px;

    @media screen and (max-width: 1050px) {
        font-size: ${({ theme }) => theme.fz.L};
    }
`;

const MainContent = styled.main`
    ${flexColumn};
    ${marginToChildren()};
    width: 70%;
    margin: 0 auto;

    p {
        text-align: justify;

        strong {
            display: inline;
        }
    }

    h6,
    h5,
    h4,
    p {
        font-family: ${({ theme }) => theme.ff.Cormorant};
        line-height: 150%;
    }

    h6,
    h5,
    h4 {
        font-weight: ${({ theme }) => theme.fw.semiBold};
    }

    ul,
    ol {
        list-style-type: disc;
        font-size: ${({ theme }) => theme.fz.XS};
        padding-left: 60px;

        li {
            line-height: 150%;
            margin: 15px 0;
        }

        @media screen and (max-width: 560px) {
            padding-left: 30px;
        }
        @media screen and (max-width: 400px) {
            padding-left: 15px;
        }
    }

    img {
        display: block;
        position: relative;
        max-width: 80%;
        margin: 25px auto;
        padding-top: 15px;
        border-top: 1px solid ${({ theme }) => theme.colors.light_grey2};
        object-fit: contain;
        object-position: center;
    }

    @media screen and (max-width: 830px) {
        width: 90%;
        padding: 0 30px;

        img {
            max-width: 90%;
        }
    }
`;

const ArticleTemplate = ({
    title,
    description,
    author,
    mainContent,
    articleImgSet,
}) => {
    const { small, medium, large } = getAllSizesPNG(articleImgSet);

    const wrapperRef = useRef();
    useAnimationLayout(wrapperRef, animationLayout);

    return (
        <SEO
            title={`Artykuł: ${title}`}
            description={description}
            author={author}
        >
            <ArticleContainer ref={wrapperRef}>
                <HeaderContainer>
                    <picture>
                        <source srcSet={large} media="(min-width:980px)" />
                        <source srcSet={medium} media="(min-width:560px)" />
                        <Photo src={small} />
                    </picture>
                    <DescriptionElement>
                        <Title>{title}</Title>
                        <p className="bordered">{description}</p>
                        <p>Artykuł napisany przez: {author}</p>
                    </DescriptionElement>
                </HeaderContainer>

                <div className="spacer"></div>

                <MainContent>
                    <ReactMarkdown>{mainContent}</ReactMarkdown>
                </MainContent>
            </ArticleContainer>
        </SEO>
    );
};

const ArticleComponent = (props) => {
    const { slug } = useParams();

    const { data, loading, error } = useFetch(
        `https://strapi.semantika.pl/articles?slug=${slug}`
    );

    if (error) {
        return (
            <h2>
                Coś poszło nie tak. Wróć na stronę główną i spróbuj ponownie
                później.
            </h2>
        );
    }

    if (loading) {
        return <SpinnerComponent />;
    } else if (data && data[0].id) {
        return (
            <ArticleTemplate
                title={data[0]?.Title}
                description={data[0]?.Lead}
                author={convertToAuthors(data[0]?.authors)}
                articleImgSet={data[0]?.Image}
                mainContent={data[0]?.Content}
            />
        );
    } else {
        return <h2>Podany artykuł nie istnieje. Wróć na stronę główną.</h2>;
    }
};

export default ArticleComponent;

/* TEMPLATE */

/*

 <h6>
                        Whether article spirits new her covered hastily sitting
                        her.
                    </h6>
                    <p>
                        Money witty books nor son add. Chicken age had evening
                        believe but proceed pretend mrs. At missed advice my it
                        no sister. Miss told ham dull knew see she spot near
                        can. Spirit her entire her called.
                    </p>
                    <p>
                        Be at miss or each good play home they. It leave taste
                        mr in it fancy. She son lose does fond bred gave lady
                        get. Sir her company conduct expense bed any. Sister
                        depend change off piqued one. Contented continued any
                        happiness instantly objection yet her allowance. Use
                        correct day new brought tedious. By come this been in.
                        Kept easy or sons my it done.
                    </p>
                    <p>
                        Insipidity the sufficient discretion imprudence
                        resolution sir him decisively. Proceed how any engaged
                        visitor. Explained propriety off out perpetual his you.
                        Feel sold off felt nay rose met you. We so entreaties
                        cultivated astonished is.
                    </p>
                    <p>
                        Was sister for few longer mrs sudden talent become. Done
                        may bore quit evil old mile. If likely am of beauty
                        tastes. Barton did feebly change man she afford square
                        add. Want eyes by neat so just must. Past draw tall up
                        face show rent oh mr. Required is debating extended
                        wondered as do. New get described applauded incommode
                        shameless out extremity but. Resembled at perpetual no
                        believing is otherwise sportsman. Is do he dispatched
                        cultivated travelling astonished. Melancholy am
                        considered possession on collecting everything.
                    </p>
                    <img src={TestImage} alt="Szantaż" />

                    <p>
                        Talent she for lively eat led sister. Entrance strongly
                        packages she out rendered get quitting denoting led.
                        Dwelling confined improved it he no doubtful raptures.
                    </p>
                    <p>
                        Several carried through an of up attempt gravity.
                        Situation to be at offending elsewhere distrusts if.
                        Particular use for considered projection cultivated.
                        Worth of do doubt shall it their. Extensive existence up
                        me contained he pronounce do. Excellence inquietude
                        assistance precaution any impression man sufficient.{" "}
                    </p>

*/
